import { useCheckoutFlow } from "../../features/checkout/useCheckoutFlow";
import type { CheckoutResource } from "../../features/checkout/useCheckoutFlow";
import {
  isDispatchProPayment,
  isDispatchProV2Payment,
  isQualpayPayment,
} from "../../features/checkout/payment";
import { FinixPaymentPanel } from "./FinixPaymentPanel";
import { PaymentAmountOptions } from "./PaymentAmountOptions";
import { PaymentConfirmationStep } from "./PaymentConfirmationStep";
import { PaymentSuccessStep } from "./PaymentSuccessStep";
import { QualpayPaymentPanel } from "./QualpayPaymentPanel";
import { XplorPaymentPanel } from "./XplorPaymentPanel";
import "./PaymentPanel.css";

type Props = {
  resource: CheckoutResource;
};

export function PaymentPanel({ resource }: Props) {
  const flow = useCheckoutFlow(resource);
  const {
    actions,
    canPay,
    customAmount,
    paymentAmount,
    paymentSubmission,
    selectedAmount,
    showCustomAmountError,
    step,
  } = flow;
  const { accountBalance, customerId, invoice, payment } = resource;

  if (step.type === "success") {
    return (
      <section class="payment-panel" aria-label="Payment">
        <PaymentSuccessStep success={step.success} onReset={actions.resetAfterSuccess} />
      </section>
    );
  }

  if (!canPay) return null;

  if (step.type === "amount") {
    return (
      <section class="payment-panel" aria-label="Payment" data-checkout-step="amount">
        <form
          class="payment-amount-form"
          onSubmit={(event) => {
            event.preventDefault();
            actions.submitAmount();
          }}
        >
          <PaymentAmountOptions
            invoice={invoice}
            accountBalance={accountBalance}
            selected={selectedAmount}
            customAmount={customAmount}
            showCustomAmountError={showCustomAmountError}
            onSelectedChange={actions.selectAmount}
            onCustomAmountChange={actions.changeCustomAmount}
            onCustomAmountTouched={actions.touchCustomAmount}
          />

          <button
            class="primary-action"
            type="submit"
            disabled={Boolean(paymentAmount.customAmountError)}
          >
            Continue to card details
          </button>
        </form>
      </section>
    );
  }

  const onBack = actions.returnToAmount;

  if (isQualpayPayment(payment))
    return step.type === "confirm" ? (
      <PaymentConfirmationStep
        error={paymentSubmission.error}
        payment={payment}
        paymentAmount={step.paymentAmount}
        pendingPayment={step.pendingPayment}
        submitting={paymentSubmission.submitting}
        onBack={actions.returnToCard}
        onSubmit={actions.submitPayment}
      />
    ) : (
      <QualpayPaymentPanel
        customerId={customerId}
        invoiceId={invoice?.id}
        payment={payment}
        paymentAmount={step.paymentAmount}
        onBack={onBack}
        onPaymentReady={actions.acceptProviderPayment}
      />
    );

  if (isDispatchProPayment(payment))
    return step.type === "confirm" ? (
      <PaymentConfirmationStep
        error={paymentSubmission.error}
        payment={payment}
        paymentAmount={step.paymentAmount}
        pendingPayment={step.pendingPayment}
        submitting={paymentSubmission.submitting}
        onBack={actions.returnToCard}
        onSubmit={actions.submitPayment}
      />
    ) : (
      <FinixPaymentPanel
        customerId={customerId}
        invoiceId={invoice?.id}
        payment={payment}
        paymentAmount={step.paymentAmount}
        onBack={onBack}
        onPaymentReady={actions.acceptProviderPayment}
      />
    );

  if (isDispatchProV2Payment(payment))
    return step.type === "confirm" ? (
      <PaymentConfirmationStep
        error={paymentSubmission.error}
        payment={payment}
        paymentAmount={step.paymentAmount}
        pendingPayment={step.pendingPayment}
        submitting={paymentSubmission.submitting}
        onBack={actions.returnToCard}
        onSubmit={actions.submitPayment}
      />
    ) : (
      <XplorPaymentPanel
        customerId={customerId}
        invoiceId={invoice?.id}
        initialZipCode={invoice?.billToAddress.postalCode}
        payment={payment}
        paymentAmount={step.paymentAmount}
        onBack={onBack}
        onPaymentReady={actions.acceptProviderPayment}
      />
    );

  return (
    <section class="payment-panel" aria-label="Payment" role="alert">
      <div class="payment-error-result">
        <h3>Payment unavailable</h3>
        <p>Payment form could not be loaded. Please try again later.</p>
      </div>
    </section>
  );
}
